/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AbstractFormComponent, FormFlowState } from '@gh/builder-shared';
import { BuilderPermission } from '@gh/builder-shared-data';
import { createDefaultGridModel, ROW_DIRTY_CLASS } from '@gh/common-internal-shared';
import {
  ActionFlow,
  ContainerState,
  getRouteInitData,
  getRouteStaticData,
  HelpID,
  ImmutablePageState,
  queryDirty,
  queryFormMode,
  State,
  UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { FocusedCell, GridDefinition, GridDefinitionFactory } from '@gh/core-ui-grid';
import { toBoolean } from '@gh/core-util';
import { of$ } from '@gh/rx';
import { switchMap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  getPbLinkCcElementId,
  getPbLinkCostCenterId,
  PbLinkCcElement,
  PbLinkCostCenter,
  PbLinkEntryPoint,
  PbLinkRecord,
} from '../../resources/price-book-link.model';
import { PriceBookLinkCommandService } from './price-book-link-command.service';
import { PriceBookLinkCsmGridDefinition } from './price-book-link-csm-grid-definition';
import { priceBookLinkCsmReducer } from './price-book-link-csm.reducers';
import { PriceBookLinkJstGridDefinition } from './price-book-link-jst-grid-definition';
import { priceBookLinkJstReducer } from './price-book-link-jst.reducers';
import {
  PRICE_BOOK_LINK_CSM_INIT_STATE,
  PRICE_BOOK_LINK_JST_INIT_STATE,
  PriceBookLinkState,
  queryPbLinkCcElements,
  queryPbLinkCostCenters,
  queryPbLinkCurrentRecords, queryPbLinkCurrentVisibleRecords,
  queryPbLinkEntryPoint,
  queryPbLinkPriceBookName,
  queryPbLinkRecordFocusedCell,
  queryPbLinkSelectedCostCenterId,
  queryPbLinkSelectedElement,
  queryPbLinkSelectedElementId, queryPbLinkShowDeleted,
} from './price-book-link.state';

export function priceBookLinkStateFactory(route: ActivatedRoute): State<PriceBookLinkState> {
  const entryPoint = getRouteStaticData(route)['entryPoint'];
  if (entryPoint === PbLinkEntryPoint.Jst) {
    return new State(PRICE_BOOK_LINK_JST_INIT_STATE, priceBookLinkJstReducer);
  } else {
    return new State(PRICE_BOOK_LINK_CSM_INIT_STATE, priceBookLinkCsmReducer);
  }
}

@Component({
  selector: 'gh-price-book-link',
  template: require('./price-book-link.component.html'),
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    PriceBookLinkCommandService,
    PriceBookLinkJstGridDefinition,
    PriceBookLinkCsmGridDefinition,
    { provide: State, useFactory: priceBookLinkStateFactory, deps: [ActivatedRoute] },
  ],
})
export class PriceBookLinkComponent extends AbstractFormComponent<PriceBookLinkState> implements OnInit {
  form: ContainerState<PriceBookLinkState>;

  ccGridDefinition: GridDefinition;
  elementGridDefinition: GridDefinition;
  linksGridDefinition: GridDefinition;

  costCenters$: Observable<PbLinkCostCenter[]>;
  selectedCostCenterId$: Observable<Maybe<ID>>;
  elements$: Observable<PbLinkCcElement[]>;
  selectedElementId$: Observable<Maybe<ID>>;
  records$: Observable<PbLinkRecord[]>;
  recordFocusedCell$: Observable<Maybe<FocusedCell>>;
  currentElement$: Observable<Maybe<PbLinkCcElement>>;

  isShowDeleted$: Observable<boolean>;

  helpId: HelpID;

  constructor(private route: ActivatedRoute,
              private state: State<PriceBookLinkState>,
              private commands: PriceBookLinkCommandService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private userContext: UserContext,
              private linksJstGridDefinition: PriceBookLinkJstGridDefinition,
              private linksCsmGridDefinition: PriceBookLinkCsmGridDefinition) {
    super();
  }

  get pageTitle(): string {
    return {
      [PbLinkEntryPoint.Jst]: 'lbl_link_with_specification_templates',
      [PbLinkEntryPoint.Csm]: 'lbl_link_with_color_selection_masters',
    }[this.state.project(queryPbLinkEntryPoint)];
  }

  get priceBookName(): string {
    return this.state.project(queryPbLinkPriceBookName);
  }

  get completeFlow(): ActionFlow<FormFlowState<PriceBookLinkState>> {
    return this.commands.completeFlow();
  }

  ngOnInit(): void {
    const { init } = getRouteInitData(this.route);

    this.form = new ImmutablePageState(this.state, {
      formMode: queryFormMode,
      dirty: queryDirty,
    });

    const mode = this.userContext.hasPermission(BuilderPermission.Estimating.PriceBook.EDIT)
      ? FormMode.EditMode : FormMode.ViewMode;

    this.buildGridDefinitions();

    this.commands.load(init, mode);

    this.selectedCostCenterId$ = this.state.select(queryPbLinkSelectedCostCenterId);
    this.selectedElementId$ = this.state.select(queryPbLinkSelectedElementId);
    this.recordFocusedCell$ = this.state.select(queryPbLinkRecordFocusedCell);

    this.costCenters$ = this.state.select(queryPbLinkCostCenters);
    this.elements$ = this.selectedCostCenterId$.pipe(switchMap$((costCenterId) =>
      costCenterId ? this.state.select((s) => queryPbLinkCcElements(s, costCenterId)) : of$([])));
    this.isShowDeleted$ = this.state.select(queryPbLinkShowDeleted);
    this.records$ = this.isShowDeleted$
      .pipe(switchMap$((isShowDeleted) => this.state.select(queryPbLinkCurrentVisibleRecords, isShowDeleted)));

    this.currentElement$ = this.state.select(queryPbLinkSelectedElement);

    this.helpId = {
      [PbLinkEntryPoint.Jst]: 'estimating/price-books/links/st',
      [PbLinkEntryPoint.Csm]: 'estimating/price-books/links/csm',
    }[this.state.project(queryPbLinkEntryPoint)];
  }

  onCostCenterSelected(id: ID): void {
    this.commands.selectCostCenter(id);
  }

  onElementSelected(id: ID): void {
    this.commands.selectElement(id);
  }

  onRecordFocusedCellChanged(_: ID): void {
    this.commands.resetRecordFocusedCell();
  }

  onReset(): void {
    this.commands.reset();
  }

  onToggleShowDeleted(): void {
    this.commands.toggleShowDeleted();
  }

  private buildGridDefinitions(): void {
    const { ccGridLayout, elementsGridLayout, linksGridLayout } = getRouteInitData(this.route);

    this.ccGridDefinition = this.gridDefinitionFactory.buildLayoutBasedWithModel({
      model: createDefaultGridModel({
        getId: getPbLinkCostCenterId,
      }),
      options: {
        rowSelection: 'single',
        getRowNodeId: (data: PbLinkCostCenter) => data.id,
        rowClassRules: {
          [ROW_DIRTY_CLASS]: ({ data }: { data: PbLinkCostCenter }) => toBoolean(data.isDirty),
        },
      },
      layout: ccGridLayout,
    });

    this.elementGridDefinition = this.gridDefinitionFactory.buildLayoutBasedWithModel({
      model: createDefaultGridModel({
        getId: getPbLinkCcElementId,
      }),
      options: {
        rowSelection: 'single',
        getRowNodeId: (data: PbLinkCcElement) => data.itemId,
        rowClassRules: {
          [ROW_DIRTY_CLASS]: ({ data }: { data: PbLinkCcElement }) => toBoolean(data.isDirty),
        },
      },
      layout: elementsGridLayout,
    });

    const linksGridDefinition = {
      [PbLinkEntryPoint.Jst]: this.linksJstGridDefinition,
      [PbLinkEntryPoint.Csm]: this.linksCsmGridDefinition,
    }[this.state.project(queryPbLinkEntryPoint)];

    linksGridDefinition.bind(linksGridLayout);

    this.linksGridDefinition = linksGridDefinition;
  }
}
